/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, ArrowRight, Eye, FileText } from 'lucide-react';

interface DataPreviewProps {
  data: {
    headers: string[];
    rows: any[][];
    filename: string;
  };
  onConfirm: () => void;
  onBack: () => void;
}

export function DataPreview({ data, onConfirm, onBack }: DataPreviewProps) {
  const { headers, rows, filename } = data;
  const previewRows = rows.slice(0, 20); // Show first 20 rows
  const totalRows = rows.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary" />
            Data Preview
          </CardTitle>
          <CardDescription>
            Review your data before mapping fields. Showing first 20 rows of{' '}
            {totalRows} total rows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{filename}</span>
            </div>
            <Badge variant="secondary">{totalRows} rows</Badge>
            <Badge variant="secondary">{headers.length} columns</Badge>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-96">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 bg-muted">#</TableHead>
                    {headers.map((header, index) => (
                      <TableHead key={index} className="bg-muted min-w-32">
                        {header || `Column ${index + 1}`}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      <TableCell className="font-mono text-xs text-muted-foreground bg-muted/50">
                        {rowIndex + 1}
                      </TableCell>
                      {headers.map((_, colIndex) => (
                        <TableCell key={colIndex} className="max-w-48">
                          <div
                            className="truncate"
                            title={String(row[colIndex] || '')}
                          >
                            {String(row[colIndex] || '')}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {totalRows > 20 && (
            <div className="text-center text-sm text-muted-foreground mt-4">
              Showing 20 of {totalRows} rows. All data will be processed in the
              next step.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Upload
        </Button>
        <Button onClick={onConfirm}>
          Continue to Field Mapping
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
